import { Component, ElementRef, forwardRef, HostBinding, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { ClickDirective } from '../../directives/click.directive';
import { MatButtonModule } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgIf, NgClass } from '@angular/common';

function parseNumber<T>(value: any, def: T): number | T {
    if (typeof value === 'string') {
        value = parseFloat(value);
    } else if (typeof value !== 'number') {
        value = def;
    }

    return isNaN(value) ? def : value;
}

@Component({
    selector: 'app-input-number-new',
    templateUrl: './input-number-new.component.html',
    styleUrls: ['./input-number-new.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputNumberNewComponent),
            multi: true
        }
    ],
    standalone: true,
    imports: [NgIf, NgClass, ExtendedModule, MatButtonModule, ClickDirective, MatIconModule]
})
export class InputNumberNewComponent implements ControlValueAccessor {
    options = {
        step: 1,
        min: null,
        max: null,
        disabled: false,
        readonly: false
    };

    @HostBinding('class.input-number') class = true;

    @Input() size: 'sm' | 'lg' = null;

    @Input() layout: 'input-1' | 'input-2' | 'input-3' | 'input-4' | 'input-5' = 'input-1';

    @Input() withAddToCart: boolean = false;
    @Output() addToCart: EventEmitter<boolean> = new EventEmitter()

    @Input() set step(value: number) {
        let temp = parseNumber(value, 1);
        this.options.step = temp < 1 ? 1 : temp;
    }

    @Input() set min(value: number) {
        let temp = parseNumber(value, 1);
        this.options.min = temp < 1 ? 1 : temp;
    }

    @Input() set max(value: number) {
        let temp = parseNumber(value, 0);
        this.options.max = temp == 0 ? null : temp
    }

    @Input() set disabled(value: boolean) {
        this.options.disabled = !!value;
    }

    @Input() set readonly(value: boolean) {
        this.options.readonly = !!value;
    }

    @Output() public updatedItem = new EventEmitter<any>();
    debouncer: Subject<any> = new Subject<any>();

    @ViewChild('inputElement') set inputElementRef(inputElementRef: ElementRef) {
        if (inputElementRef) { // initially setter gets called with undefined
            this.inputElement = inputElementRef.nativeElement;
        }
    }
    inputElement: any = {};

    get value(): '' | number {
        return this.inputElement?.value === '' ? '' : parseFloat(this.inputElement?.value);
    }
    set value(value: '' | number) {
        this.writeValue(value);
    }

    onChange = (_: any) => { };
    onTouched = () => { };

    constructor() {
        this.debouncer.pipe(
            debounceTime(1000)
        ).subscribe((value) => this.updatedItem.emit(value));
    }

    add(): void {
        this.change(1);
        this.changeByTimer(1);
    }

    sub(): void {
        this.change(-1);
        this.changeByTimer(-1);
    }

    input(): void {
        if (this.value) {
            this.debouncer.next(this.value);
        }
        this.onChange(this.value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(obj: any): void {
        if (typeof obj === 'number') {
            this.inputElement.value = obj.toString();
        } else {
            this.inputElement.value = '';
        }
    }

    /**
     * @param direction - one of [-1, 1]
     */
    private change(direction: number): void {
        let value = (this.value === '' || isNaN(this.value) ? 0 : this.value) + this.options.step * direction;

        if (this.options.max !== null) {
            value = Math.min(this.options.max, value);
        }
        if (this.options.min !== null) {
            value = Math.max(this.options.min, value);
        }

        if (value !== this.value) {
            this.onChange(value);
            this.value = value;
        }

        this.debouncer.next(value);
    }

    /**
     * @param direction - one of [-1, 1]
     */
    private changeByTimer(direction: number): void {
        let interval;
        const timer = setTimeout(() => {
            interval = setInterval(() => this.change(direction), 50);
        }, 300);

        const documentMouseUp = () => {
            clearTimeout(timer);
            clearInterval(interval);

            document.removeEventListener('mouseup', documentMouseUp, false);
            document.removeEventListener('touchend', documentMouseUp, false);

        };

        document.addEventListener('mouseup', documentMouseUp, false);
        document.addEventListener('touchend', documentMouseUp, false);

    }

    checkDecimal(event) {
        if (event.key === '.') { event.preventDefault(); }
    }
}
